<template>
  <div class="banner-wrap">
    <!-- ================ -->
    <!-- left-hand menu navigation for small devices -->
    <!-- ================ -->
    <v-navigation-drawer v-model="drawer" app temporary>
      <v-subheader tabindex="-1" ref="menuHeader" class="hide-focus">Menu</v-subheader>
      <v-list>
        <v-list-tile :to="{ name: 'home' }" active-class>
          <v-list-tile-action>
            <v-icon>home</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>
              {{ $t('home.title') }}
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-group v-model="searchExpanded">
          <v-list-tile slot="activator" @click.prevent href="#">
            <v-list-tile-action>
              <v-icon>search</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('global.search') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile v-for="(value, key) in $PACONST.searchTypes"
                       v-if="hasAccess(value.componentID, $PACONST.PERMISSION_TYPES.READ)"
                       :key="key"
                       active-class
                       :to="{ name: 'search', params: { searchType: key} }">
            <v-list-tile-content>
              <v-list-tile-title>
                <span class="sr-only">{{ $t('global.goTo') }}</span>
                {{ $t('search.types.' + key) }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list-group>
      </v-list>
    </v-navigation-drawer>

    <!-- ================ -->
    <!-- main toolbar and menu navigation -->
    <!-- ================ -->
    <v-toolbar color="secondary"
               dark
               absolute
               app
               :extension-height="$vuetify.breakpoint.smAndDown ? '0' : this.menuHeight"
               :class="{ 'banner-accent': $vuetify.breakpoint.smAndDown }">
      <v-toolbar-side-icon v-if="isLoggedIn && $vuetify.breakpoint.smAndDown" @click="drawer = !drawer"/>
      <v-toolbar-title>
        <img class="hidden-sm-and-down mr-2"
             src="~@custom/assets/splash-small.png"
             alt=""
             style="vertical-align:middle;"
             aria-hidden="true"/>
        {{ $t('app.title') }}
      </v-toolbar-title>
      <v-spacer/>

      <v-toolbar-items v-if="isLoggedIn" class="mr-3">
        <v-menu offset-y dense>
          <v-btn flat slot="activator">
            {{ displayName }} <v-icon dark>arrow_drop_down</v-icon>
            <span class="sr-only">{{ $t('banner.userMenu') }}</span>
          </v-btn>
          <v-list dense :light="true">
            <v-list-tile @click="logout">
              <v-list-tile-title>{{ $t('logout.button') }}</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-toolbar-items>

      <v-text-field v-if="isLoggedIn"
                    flat
                    solo-inverted
                    prepend-inner-icon="search"
                    @click:prepend-inner="quickSearch"
                    :maxlength="250"
                    :label="$t('search.quick.placeholder')"
                    class="hidden-sm-and-down"
                    style="max-width:250px"
                    hide-details
                    v-model.trim="queryString"
                    :placeholder="$t('search.quick.placeholder')"
                    @keydown.native.enter="quickSearch"/>

      <v-toolbar color="accent" class="hidden-sm-and-down" :height="menuHeight" slot="extension">
        <v-toolbar-items class="menu-bar">
          <template v-if="isLoggedIn">
            <v-btn flat :to="{ name: 'home' }" active-class>
              {{ $t('home.title') }}
            </v-btn>
            <v-menu offset-y dense>
              <v-btn flat slot="activator">
                Search <v-icon dark>arrow_drop_down</v-icon>
              </v-btn>
              <v-list dense :light="true">
                <v-list-tile v-for="(value, key) in $PACONST.searchTypes"
                             :key="key"
                             active-class
                             v-if="hasAccess(value.componentID, $PACONST.PERMISSION_TYPES.READ)"
                             :to="{ name: 'search', params: { searchType: key} }">
                  <v-list-tile-title>
                    <span class="sr-only">{{ $t('global.goTo') }}</span>
                    {{ $t('search.types.' + key) }}
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </template>
          <!--          <template v-else>-->
          <!--            <v-btn flat :to="{ name: 'login' }" active-class>-->
          <!--              {{ $t('login.title') }}-->
          <!--            </v-btn>-->
          <!--          </template>-->
        </v-toolbar-items>
      </v-toolbar>
    </v-toolbar>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import Cove from '@ctrack/cove';

const { mapGetters, mapMutations } = createNamespacedHelpers('user');

export default {
  data () {
    return {
      drawer: false,
      searchExpanded: false,
      queryString: '',
      menuHeight: 36
    };
  },
  computed: mapGetters(['hasAccess', 'isLoggedIn', 'displayName']),
  created () {
    window.addEventListener('keydown', (event) => {
      if (event.keyCode === 27) // esc key
      {
        this.drawer = false;
      }
    });
  },
  watch: {
    drawer (newValue) {
      if (newValue)
      {
        this.$refs.menuHeader.focus();
      }
    }
  },
  methods: {
    ...mapMutations(['logout']),
    quickSearch () {
      let searchFieldData = {
        'searchFields[0].searchType': 'Starts With',
        'searchFields[0].operation': '=',
        'searchFields[0].values[0]': this.queryString,
        'searchFields[0].indexFieldName': 'caseNumber'
      };

      let pagination = new Cove.Pagination({});

      this.$router.push({
        name: 'search',
        params: {
          searchType: 'case'
        },
        query: {
          q: true,
          advanced: false,
          ...searchFieldData,
          ...pagination.generateURLParams(),
          _: new Date().getTime()
        }
      });
      this.queryString = '';
    }
  }
};
</script>

<style lang="scss" scoped>
// fixes banner/extension padding issues
.banner-wrap /deep/ .v-toolbar__extension,
.banner-wrap /deep/ .v-toolbar__extension .v-toolbar__content {
  padding: 0;
}

// additional space for search icon in quicksearch
/deep/ .v-text-field .v-input__prepend-inner {
  padding-right: 8px;
}

.banner-accent {
  border-bottom: 4px solid $c-accent !important;
}

.hide-focus:focus {
  outline: none;
}

@if $banner-menu-shadow {
  .menu-bar a,
  .menu-bar button {
    text-shadow: 1px 1px 1px #444;
  }
}
</style>
